// View as customer (ENG-2337). The server refuses every write with
// 403 { code: "view_as_read_only" }, but each screen reports a failed request
// in its own words ("Failed to create chat. Please try again later."), which
// is wrong here: retrying never works. While a session is live, fetch is
// wrapped so a refusal shows the server's view-only message instead, and the
// one generic error toast that follows it is dropped. The guard also catches
// 409 { code: "view_as_ended" } from any endpoint: the server has already
// dropped the session, so the tab must leave the customer's view rather than
// let the next write run as the employee.

import { leaveEndedViewAs } from "./viewAsIdentity";

const REFUSAL_CODE = "view_as_read_only";
const ENDED_CODE = "view_as_ended";
const SUPPRESS_WINDOW_MS = 5000;

let lastRefusalAt = 0;

/** True just after a view-only refusal, while call sites raise their own error. */
export function isRecentViewOnlyRefusal(now: number = Date.now()): boolean {
  return now - lastRefusalAt < SUPPRESS_WINDOW_MS;
}

/**
 * Claims the one generic error that follows a refusal. Later errors, even
 * within the window, are unrelated failures and must still show.
 */
export function consumeViewOnlyRefusal(now: number = Date.now()): boolean {
  if (!isRecentViewOnlyRefusal(now)) return false;
  lastRefusalAt = 0;
  return true;
}

type ShowRefusal = (message: string) => void;

/**
 * Wraps window.fetch until the returned function is called. The response body
 * is read from a clone before the caller sees the response, so the refusal is
 * recorded before the caller's own error handling runs.
 */
export function installViewOnlyFetchGuard(showRefusal: ShowRefusal): () => void {
  const originalFetch = window.fetch;

  window.fetch = async (...args: Parameters<typeof fetch>) => {
    const response = await originalFetch(...args);
    if (response.status === 403 || response.status === 409) {
      const body = await response
        .clone()
        .json()
        .catch(() => null);
      if (response.status === 403 && body?.code === REFUSAL_CODE) {
        lastRefusalAt = Date.now();
        showRefusal(typeof body.error === "string" ? body.error : "This is a view-only session.");
      } else if (response.status === 409 && body?.code === ENDED_CODE) {
        leaveEndedViewAs(body.location);
      }
    }
    return response;
  };

  return () => {
    window.fetch = originalFetch;
  };
}
